import { computed, nextTick, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useViewManagerStore } from '@/common/stores/view-manager';
import {
  FilterData,
  useFilterListStore,
  isFilter,
  Filter,
  isFilterData,
} from '@/common/stores/filter-list';
import { makeRowToObj } from '@/common/utils/gridUtils';
import { useTimePeriodStore } from '@/common/stores/main-view-time-period';
import { LocationQueryValue, useRoute, useRouter } from 'vue-router';
import { TARGET_TYPE, useAlertTagsAndTargets } from '@/alert/utils/tagsAndTargets.uses';
import { useInternational } from '@/common/locale';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { ON_SITE_ALERT_FROM } from '@/common/components/organisms/onSiteAlarm/onSiteAlarm.setup';
import { useAlertRuleDetailAdapter } from '@/alert/adapter/alertRuleDetail.adapter';
import { useTargetTagStore } from '@/common/stores/target-tags';
import { IconType } from '@/types/icon.types';

import {
  usePostV9AlertsAlertGroupsAlertRules,
  useGetV9AlertsAlertRulesExternalErrorByAlertRuleId,
} from '@/api-client/alert/queries/alert-rule-v9-controller/alert-rule-v9-controller';
import { useVueQueryErrorHandler } from '@/common/composables/useVueQueryErrorHandler';
import {
  statusCardInfoGetter,
  statusFilterCallback,
} from '@/common/components/molecules/statusCardGroup/statusCard.utils';
import type {
  AlertStatusType,
  StatusCardGroupInfo,
  TotalStatusType,
} from '@/common/components/molecules/statusCardGroup/statusCard.types';
import { AlertRuleV9Item } from '@/api-client/alert/model';
import { ALERT_VIEW_GRID_COLUMNS } from './alertView.define';

export const ALERT_TYPE_ICON: Record<string, IconType> = {
  Prometheus: 'icon-prometheus',
  Elasticsearch: 'icon-elasticsearch',
};

const useGlobalTime = () => {
  const timePeriodStore = useTimePeriodStore();
  const { timePeriodInfo } = storeToRefs(timePeriodStore);
  const { path } = useRoute();

  const updateIsPaused = (isPaused: boolean) => {
    timePeriodInfo.value[path].isPaused = isPaused;
  };

  return {
    updateIsPaused,
  };
};

export const setup = () => {
  const { t } = useInternational();
  const { errorHandlerWithMessage } = useVueQueryErrorHandler();

  const viewManagerStore = useViewManagerStore();
  const { isPageLoading } = storeToRefs(viewManagerStore);
  const { checkedFilterListData } = storeToRefs(useFilterListStore());
  const { fetchAllData: fetchAllTagsData } = useTargetTagStore();

  const { makeAlertRows } = useAlertTagsAndTargets();
  const { isSlideDetailOpened, openAlertRuleDetail } = useAlertRuleDetailAdapter();
  const { updateIsPaused } = useGlobalTime();

  const alertGroupIds = ref<string[]>([]);
  const rawAlertData = ref<AlertRuleV9Item[]>([]);
  const selectedStatus = ref<TotalStatusType | AlertStatusType>('total');
  const statusCards = computed<StatusCardGroupInfo<AlertStatusType>>(() =>
    statusCardInfoGetter(rawAlertData.value, {
      alertKey: 'alertLevel',
      alertFields: ['normal', 'warning', 'critical'],
    }),
  );
  const gridColumns = ALERT_VIEW_GRID_COLUMNS;
  const filteredGridData = computed<any[][]>(() => {
    const filteredData = rawAlertData.value.filter((data) =>
      statusFilterCallback(selectedStatus.value, data.alertLevel),
    );

    return makeAlertRows({
      data: filteredData,
      columns: gridColumns,
      targetFieldName: 'tags',
      targetType: TARGET_TYPE.TAGS,
    });
  });
  const isShowViewer = ref<boolean>(false);
  const errorMessage = ref<string>('');
  const selectedAlertRuleIdForError = ref<string | null>(null);

  const { mutateAsync: fetchAlertRules } = usePostV9AlertsAlertGroupsAlertRules({
    request: {
      params: { frameName: FRAME_NAMES.ALERT_VIEW.LIST },
    },
    mutation: {
      onError: (error: unknown) => {
        errorHandlerWithMessage({ error });
      },
    },
  });

  const { data: externalErrorData, error: externalErrorError } =
    useGetV9AlertsAlertRulesExternalErrorByAlertRuleId(
      computed(() => selectedAlertRuleIdForError.value || ''),
      {
        query: {
          enabled: computed(() => !!selectedAlertRuleIdForError.value),
          select: ({ data: { data = [] } }) => data?.[0]?.message || '',
        },
        request: {
          params: {
            frameName: FRAME_NAMES.ALERT_VIEW.LIST,
          },
        },
      },
    );

  watch(externalErrorData, (message) => {
    if (message) {
      errorMessage.value = message;
    }
  });

  watch(externalErrorError, (error) => {
    errorHandlerWithMessage({ error });
  });

  const fetchData = async () => {
    if (!alertGroupIds.value?.length) {
      viewManagerStore.setLoading(false);
      rawAlertData.value = [];
      return;
    }

    try {
      const {
        data: { data = [] },
      } = await fetchAlertRules({
        data: alertGroupIds.value,
      });

      rawAlertData.value = data ?? [];
    } catch (error) {
      console.log(error);
    } finally {
      viewManagerStore.setLoading(false);
    }
  };

  const onClickIndicator = async () => {
    await fetchData();
  };
  const onClickCell = ({ field, row }: { field: string; row: any }) => {
    if (field === 'alertRuleName') {
      const { alertRuleName, alertRuleType, alertRuleId } = row;
      openAlertRuleDetail({
        params: { alertRuleId, alertRuleName, alertRuleType },
        init: true,
      });
    }
  };

  const onClickError = async (item) => {
    const row = makeRowToObj(item.row?.[2], gridColumns);
    if (row.isError) {
      isShowViewer.value = true;
      selectedAlertRuleIdForError.value = row.alertRuleId;
    }
  };

  watch(isSlideDetailOpened, (isVisible) => {
    updateIsPaused(isVisible);
  });

  watch(checkedFilterListData, async (data: FilterData[]) => {
    const extractAllFilters = (items: FilterData[] | Filter[]): Filter[] => {
      const filters: Filter[] = [];

      items.forEach((item) => {
        if (isFilter(item)) {
          filters.push(item);
        } else if (isFilterData(item)) {
          filters.push(...extractAllFilters(item.values));
        }
      });

      return filters;
    };

    const values = data.flatMap((item) => extractAllFilters(item.values));
    alertGroupIds.value = values.map((v) => v.id);

    viewManagerStore.setLoading(true);
    await fetchData();
  });

  // 다른 화면에서 연계되는 경우
  const route = useRoute();
  const router = useRouter();

  watch(
    () => route.query.from,
    async (from) => {
      const { alertRuleId, alertRuleName, alertRuleType } = route.query as Record<
        string,
        LocationQueryValue
      >;
      if (from === ON_SITE_ALERT_FROM && alertRuleName && alertRuleType) {
        await router.replace({ path: route.path });
        await nextTick(() => {
          openAlertRuleDetail({
            params: { alertRuleName, alertRuleType, alertRuleId: alertRuleId ?? '' },
            state: { keepOpen: true },
            init: true,
          });
        });
      }
    },
    { immediate: true },
  );

  onBeforeMount(async () => {
    viewManagerStore.setLoading(true);
    await fetchAllTagsData(FRAME_NAMES.ALERT_VIEW.TAGS);
  });

  onBeforeUnmount(() => {
    viewManagerStore.setLoading(false);
  });

  return {
    t,

    isPageLoading,
    isShowViewer,
    isDetailVisible: isSlideDetailOpened,

    statusCards,
    selectedStatus,

    errorMessage,

    gridColumns,
    filteredGridData,
    ALERT_TYPE_ICON,

    onClickIndicator,
    onClickCell,
    onClickError,
  };
};
